import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiTrash, HiShoppingBag } from 'react-icons/hi';

import { useCart, cartKey } from '../context/CartContext';
import { getProducts } from '../backend/utils/orderUtils';
import { mapDbProduct } from '../utils/productMap';
import { validateCoupon } from '../services/couponApi';
import {
  couponDiscount,
  couponRule,
  describeCoupon,
  meetsMinimum,
  normaliseCode,
  shortfallMessage,
} from '../utils/coupon';
import {
  trackBeginCheckout,
  trackEvent,
  trackRemoveFromCart,
  trackViewCart,
} from '../utils/analytics';
import CheckoutModal from './CheckoutModal';
import ImageLightbox from './ImageLightbox';

import '../styles/Cart.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function Cart() {
  const {
    items,
    subtotal,
    count,
    coupon,
    discount,
    total,
    setQuantity,
    removeItem,
    clear,
    syncWithCatalogue,
    applyCoupon,
    removeCoupon,
  } = useCart();
  const navigate = useNavigate();

  /* A snapshot of what was in the cart when checkout opened. Placing the
     order empties the cart, and reading live `items` here would unmount
     the modal mid-success-screen. */
  const [checkoutLines, setCheckoutLines] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [syncing, setSyncing] = useState(true);
  const [dropped, setDropped] = useState([]); // names pulled from the catalogue

  const [codeInput, setCodeInput] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  // { text, tone: 'ok' | 'bad' } — one line under the coupon field
  const [codeNotice, setCodeNotice] = useState(null);

  // Read inside refresh() without making the catalogue reload depend on
  // the cart, which would re-fetch on every quantity change.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  /* Prices and stock in the cart come from localStorage and may be days
     old. Reload the catalogue so the customer checks out against what is
     actually on the shelf today. */
  const refresh = useCallback(async () => {
    const { success, products } = await getProducts();

    if (success && products.length > 0) {
      const catalogue = products.map(mapDbProduct);
      const live = new Set(catalogue.map(cartKey));

      // syncWithCatalogue removes these — say so rather than letting a
      // line vanish between the customer adding it and checking out.
      setDropped(
        itemsRef.current
          .filter((line) => !live.has(cartKey(line.product)))
          .map((line) => line.product.name)
      );

      syncWithCatalogue(catalogue);
    }

    setSyncing(false);
  }, [syncWithCatalogue]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /* One view_cart per visit, once the catalogue sync has settled the
     real prices — reporting the stale localStorage total would make the
     cart value in GA disagree with the order that follows it. */
  const cartViewSent = useRef(false);

  useEffect(() => {
    if (syncing || cartViewSent.current || items.length === 0) return;
    cartViewSent.current = true;
    trackViewCart(items);
  }, [syncing, items]);

  /* Both ways a coupon gets settled — the customer typing one, and the
     stored one being re-checked on load — end here. A live code whose
     basket is simply too small yet is KEPT: the summary shows how much
     more to add. Anything genuinely dead is dropped. */
  const settleCoupon = useCallback(
    async (code, amount) => {
      const result = await validateCoupon(code, amount);

      if (result.valid || result.reason === 'below_minimum') {
        applyCoupon(couponRule(result));
      } else {
        removeCoupon();
      }

      return result;
    },
    [applyCoupon, removeCoupon]
  );

  /* A code applied minutes or days ago may have expired, been switched
     off or run out since — and the catalogue sync just moved the
     subtotal it was measured against. Re-check once, after prices settle. */
  const codeChecked = useRef(false);

  useEffect(() => {
    if (syncing || codeChecked.current || !coupon) return;
    codeChecked.current = true;

    const applied = coupon.code;
    settleCoupon(applied, subtotal).then((result) => {
      if (result.valid || result.reason === 'below_minimum') return;
      setCodeNotice({ text: `${applied} is no longer valid.`, tone: 'bad' });
    });
  }, [syncing, coupon, subtotal, settleCoupon]);

  const handleApplyCode = async (e) => {
    e.preventDefault();

    const code = normaliseCode(codeInput);
    if (!code) {
      setCodeNotice({ text: 'Enter a discount code.', tone: 'bad' });
      return;
    }

    setCheckingCode(true);
    const result = await settleCoupon(code, subtotal);
    setCheckingCode(false);

    if (!result.valid && result.reason !== 'below_minimum') {
      setCodeNotice({ text: result.message, tone: 'bad' });
      return;
    }

    setCodeInput('');

    if (result.valid) {
      setCodeNotice({
        text: `${result.code} applied — ${describeCoupon(result)}.`,
        tone: 'ok',
      });
      trackEvent('apply_coupon', {
        coupon: result.code,
        value: couponDiscount(result, subtotal),
      });
    } else {
      // Below the minimum: the inline note under the code says what's
      // missing, so a second line here would only repeat it.
      setCodeNotice(null);
    }
  };

  const handleRemoveCode = () => {
    trackEvent('remove_coupon', { coupon: coupon?.code });
    removeCoupon();
    setCodeNotice(null);
  };

  const handleRemove = (line) => {
    trackRemoveFromCart(line.product, line.quantity);
    removeItem(cartKey(line.product));
  };

  const handleClear = () => {
    trackEvent('clear_cart', { item_count: count });
    clear();
  };

  const handleCheckout = () => {
    trackBeginCheckout(items, 'cart');
    setCheckoutLines(items);
  };

  const stockOf = (p) =>
    Number.isFinite(Number(p.stock)) ? Number(p.stock) : null;

  const soldOut = items.filter(
    (line) => line.product.inStock === false || stockOf(line.product) === 0
  );

  const canCheckout = items.length > 0 && soldOut.length === 0;

  const droppedNotice = dropped.length > 0 && (
    <p className="cart-dropped">
      {dropped.length === 1
        ? `"${dropped[0]}" is no longer available and was removed from your cart.`
        : `${dropped.length} items are no longer available and were removed from your cart.`}
    </p>
  );

  /* ------------------------------ empty ------------------------------ */
  if (items.length === 0 && !checkoutLines) {
    return (
      <section className="cart-page">
        <div className="cart-head">
          <button className="back-to-home-btn" onClick={() => navigate('/shop')}>
            <HiArrowLeft />
            <span>Back to Shop</span>
          </button>
          <h1>Your Cart</h1>
        </div>

        {droppedNotice}

        <div className="cart-empty">
          <HiShoppingBag />
          <p>Your cart is empty.</p>
          <span>
            Browse our lab-certified gemstones and add the ones aligned with
            your chart.
          </span>
          <Link className="cart-checkout-btn" to="/shop">
            Explore the Collection
          </Link>
        </div>
      </section>
    );
  }

  /* ------------------------------ filled ----------------------------- */
  return (
    <section className="cart-page">
      <div className="cart-head">
        <button className="back-to-home-btn" onClick={() => navigate('/shop')}>
          <HiArrowLeft />
          <span>Continue Shopping</span>
        </button>
        <h1>Your Cart</h1>
        <p>
          {count} {count === 1 ? 'item' : 'items'}
          {syncing && ' · checking latest prices…'}
        </p>
      </div>

      {droppedNotice}

      <div className="cart-layout">
        {/* ITEMS */}
        <div className="cart-items">
          {items.map((line) => {
            const key = cartKey(line.product);
            const stock = stockOf(line.product);
            const out = line.product.inStock === false || stock === 0;
            const cap = stock && stock > 0 ? stock : 99;

            return (
              <motion.div
                className={`cart-item ${out ? 'is-out' : ''}`}
                key={key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div
                  className={`cart-item-image ${line.product.image ? 'zoomable' : ''}`}
                  onClick={() =>
                    line.product.image &&
                    setZoom({ src: line.product.image, alt: line.product.name })
                  }
                  role={line.product.image ? 'button' : undefined}
                  tabIndex={line.product.image ? 0 : undefined}
                  aria-label={
                    line.product.image ? `Zoom into ${line.product.name}` : undefined
                  }
                  onKeyDown={(e) => {
                    if (line.product.image && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setZoom({ src: line.product.image, alt: line.product.name });
                    }
                  }}
                >
                  {line.product.image ? (
                    <img src={line.product.image} alt={line.product.name} />
                  ) : (
                    <div className="cart-item-fallback">
                      {line.product.emoji || '💎'}
                    </div>
                  )}
                </div>

                <div className="cart-item-info">
                  <h3>{line.product.name}</h3>
                  <p className="cart-item-planet">
                    {line.product.emoji} {line.product.planet}
                  </p>
                  <p className="cart-item-unit">{inr(line.product.price)} each</p>

                  {out ? (
                    <p className="cart-item-warn">
                      Out of stock — remove to continue
                    </p>
                  ) : (
                    stock !== null &&
                    stock <= 5 && (
                      <p className="cart-item-warn">Only {stock} left</p>
                    )
                  )}
                </div>

                <div className="cart-item-actions">
                  <div className="qty-row">
                    <button
                      type="button"
                      onClick={() => setQuantity(key, line.quantity - 1)}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span>{line.quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity(key, line.quantity + 1)}
                      disabled={out || line.quantity >= cap}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>

                  <div className="cart-item-total">
                    {inr(line.product.price * line.quantity)}
                  </div>

                  <button
                    className="cart-remove"
                    onClick={() => handleRemove(line)}
                    aria-label={`Remove ${line.product.name}`}
                  >
                    <HiTrash />
                    <span>Remove</span>
                  </button>
                </div>
              </motion.div>
            );
          })}

          <button className="cart-clear" onClick={handleClear}>
            Clear cart
          </button>
        </div>

        {/* SUMMARY */}
        <aside className="cart-summary">
          <h2>Order Summary</h2>

          <div className="cart-summary-row">
            <span>Subtotal ({count})</span>
            <span>{inr(subtotal)}</span>
          </div>

          <div className="cart-summary-row">
            <span>Shipping</span>
            <span className="cart-free">Free</span>
          </div>

          {discount > 0 && (
            <div className="cart-summary-row cart-summary-discount">
              <span>Discount ({coupon.code})</span>
              <span>−{inr(discount)}</span>
            </div>
          )}

          {/* ---------- discount code ---------- */}
          <div className="cart-coupon">
            {coupon ? (
              <>
                <div className="cart-coupon-applied">
                  <div className="cart-coupon-code">
                    <strong>{coupon.code}</strong>
                    <span>{describeCoupon(coupon)}</span>
                  </div>
                  <button type="button" onClick={handleRemoveCode}>
                    Remove
                  </button>
                </div>

                {!meetsMinimum(coupon, subtotal) && (
                  <p className="cart-coupon-note">
                    {shortfallMessage(coupon, subtotal)}
                  </p>
                )}
              </>
            ) : (
              <form className="cart-coupon-form" onSubmit={handleApplyCode}>
                <input
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="Discount code"
                  aria-label="Discount code"
                  autoComplete="off"
                  spellCheck="false"
                />
                <button type="submit" disabled={checkingCode}>
                  {checkingCode ? 'Checking…' : 'Apply'}
                </button>
              </form>
            )}

            {codeNotice && (
              <p className={`cart-coupon-msg is-${codeNotice.tone}`}>
                {codeNotice.text}
              </p>
            )}
          </div>

          <div className="cart-summary-row cart-summary-total">
            <span>Total</span>
            <strong>{inr(total)}</strong>
          </div>

          {soldOut.length > 0 && (
            <p className="cart-summary-warn">
              {soldOut.length === 1
                ? `"${soldOut[0].product.name}" is out of stock.`
                : `${soldOut.length} items in your cart are out of stock.`}{' '}
              Remove {soldOut.length === 1 ? 'it' : 'them'} to check out.
            </p>
          )}

          <button
            className="cart-checkout-btn"
            onClick={handleCheckout}
            disabled={!canCheckout}
          >
            Checkout via WhatsApp
          </button>

          <p className="cart-summary-note">
            We'll save your order first, then open WhatsApp with your order ID
            so our team can confirm payment and delivery.
          </p>
        </aside>
      </div>

      {checkoutLines && (
        <CheckoutModal
          items={checkoutLines}
          coupon={coupon}
          onClose={() => setCheckoutLines(null)}
          onPlaced={clear}
        />
      )}

      {zoom && (
        <ImageLightbox
          src={zoom.src}
          alt={zoom.alt}
          onClose={() => setZoom(null)}
        />
      )}
    </section>
  );
}
